import { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { NotebookService } from "../../../../services/NotebookService";
import { createLogger } from "../../../../utils/logger";
import "./NewNotebookModal.css";

const logger = createLogger('NewNotebookModal');
const notebookService = new NotebookService();

interface NewNotebookModalProps {
  show: boolean;
  handleClose: (result?: { success: boolean }) => void;
}

const NewNotebookModal = ({ show, handleClose }: NewNotebookModalProps) => {
  const [name, setName] = useState("");
  const [showError, setShowError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const resetForm = useCallback(() => {
    setName("");
    setShowError(false);
    setIsLoading(false);
  }, []);

  // Reset form when modal opens
  useEffect(() => {
    if (show) {
      resetForm();
    }
  }, [show, resetForm]);

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!show) return;
      
      if (event.key === "Escape") {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [show, handleClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || isLoading) return;

    setIsLoading(true);
    setShowError(false);

    try {
      const existingNotebook = await notebookService.readByName(name.trim());

      if (!existingNotebook) {
        await notebookService.createNotebook({
          name: name.trim(),
          owner_id: 1, // TODO: get from user session
        });
        handleClose({ success: true });
      } else {
        setShowError(true);
      }
    } catch (error) {
      logger.error("Error creating notebook:", error);
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!show) return null;

  return (
    <div className="new-notebook-modal__backdrop" onClick={handleBackdropClick}>
      <div className="new-notebook-modal__container">
        {/* Header */}
        <div className="new-notebook-modal__header">
          <h2 className="new-notebook-modal__title">Create New Notebook</h2>
          <button
            className="new-notebook-modal__close"
            onClick={() => handleClose()}
            aria-label="Close modal"
            type="button"
          >
            <FontAwesomeIcon icon="times" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="new-notebook-modal__form">
          <div className="new-notebook-modal__field">
            <label htmlFor="notebook-name" className="new-notebook-modal__label">
              Notebook Name
            </label>
            <input
              id="notebook-name"
              type="text"
              className="new-notebook-modal__input"
              placeholder="Enter notebook name..."
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setShowError(false);
              }}
              autoFocus
              disabled={isLoading}
              maxLength={50}
            />
            {showError && (
              <div className="new-notebook-modal__error">
                <FontAwesomeIcon icon="exclamation-triangle" />
                <span>A notebook with this name already exists</span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="new-notebook-modal__actions">
            <button
              type="button"
              className="new-notebook-modal__button new-notebook-modal__button--secondary"
              onClick={() => handleClose()}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="new-notebook-modal__button new-notebook-modal__button--primary"
              disabled={!name.trim() || isLoading}
            >
              {isLoading ? (
                <>
                  <FontAwesomeIcon icon="spinner" spin />
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon="plus" />
                  <span>Create Notebook</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewNotebookModal;
